import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Marker — a conversation status / cost marker (shadcn's conversation layer).
 *
 * Two shapes, one component:
 *   • `streaming` → a `Loader2` spinner + the `ds-shimmer` animated-text effect,
 *     for the assistant's "thinking…" turn indicator.
 *   • `variant="pill"` + `tone` → a compact bordered chip, for a status/cost
 *     badge (e.g. the KI-Hofassistent remaining-balance badge). `tone` colours
 *     the text (and, for the pill, the border + surface) neutral / warning /
 *     danger from the brand semantic scale.
 *
 * By default the Marker carries NO ARIA role — announcement is the consumer's
 * concern (a chat surface typically pairs a visible, aria-hidden marker with a
 * single static `role="status"` region so a rotating subtitle isn't re-announced
 * on every tick). Opt into a live region with `announce` when the Marker itself
 * should be read out.
 */
export type MarkerTone = 'neutral' | 'warning' | 'danger';
export type MarkerVariant = 'inline' | 'pill';

export interface MarkerProps extends React.HTMLAttributes<HTMLDivElement> {
  streaming?: boolean;
  tone?: MarkerTone;
  variant?: MarkerVariant;
  /** Add `role="status"` so the marker's content is announced. Default false. */
  announce?: boolean;
}

const toneText: Record<MarkerTone, string> = {
  neutral: 'tw:text-muted-foreground',
  warning: 'tw:text-[var(--color-warning-600)]',
  danger: 'tw:text-[var(--color-error-600)]',
};

const tonePill: Record<MarkerTone, string> = {
  neutral: 'tw:border-border tw:bg-muted',
  warning:
    'tw:border-[var(--color-warning-400)] tw:bg-[var(--color-warning-100)]',
  danger: 'tw:border-[var(--color-error-500)] tw:bg-[var(--color-error-100)]',
};

export const Marker = React.forwardRef<HTMLDivElement, MarkerProps>(
  (
    {
      className,
      streaming = false,
      tone = 'neutral',
      variant = 'inline',
      announce = false,
      children,
      ...props
    },
    ref
  ) => {
    const isPill = variant === 'pill';
    return (
      <div
        ref={ref}
        role={announce ? 'status' : undefined}
        className={cn(
          'tw:flex tw:items-center tw:gap-1.5 tw:text-xs',
          isPill
            ? cn(
                'tw:inline-flex tw:whitespace-nowrap tw:rounded-full tw:border tw:px-2.5 tw:py-0.5 tw:font-semibold',
                tonePill[tone]
              )
            : 'tw:px-1 tw:py-1',
          // Tone colours static markers (streaming uses the shimmer gradient).
          !streaming && toneText[tone],
          className
        )}
        {...props}
      >
        {streaming && (
          <Loader2 className="tw:h-3 tw:w-3 tw:animate-spin tw:text-muted-foreground" />
        )}
        {/* Streaming wraps the text in a single shimmer span; static markers
            render children as direct flex children so an icon + label are
            spaced by the row gap (e.g. the cost badge's Wallet + amount). */}
        {streaming ? <span className="ds-shimmer">{children}</span> : children}
      </div>
    );
  }
);
Marker.displayName = 'Marker';
